import React, { useEffect, useRef } from 'react';
import {
  Box,
  Divider,
  Drawer,
  DrawerProps,
  IconButton,
  Stack,
  styled,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { motion } from 'framer-motion';
import { useAppDispatch } from '@/hooks';
import { navItems } from '@/config';
import { NavigationList } from './NavigationList';
import Image from 'next/image';
import Logo from '../../../public/logo/logo_hoopcare.svg';
import { LangSelector } from '@/components/langSelect';
import LogoutIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import { useTranslator } from '@ianesthesie-kit/core';
import { User } from '@ianesthesie-kit/services';
import PersonOutlineTwoToneIcon from '@mui/icons-material/PersonOutlineTwoTone';
import { useCurrentUser, logout } from '@ianesthesie-kit/redux';
import { usePathname } from 'next/navigation';

// const sidebar = {
//   open: {
//     clipPath: `circle(2000px at 40px 40px)`,
//     transition: {
//       // delay: 1.5,
//       type: 'spring',
//       stiffness: 20,
//       velocity: 20,
//       restDelta: 2,
//       // duration: 0.5
//     }
//   },
//   closed: {
//     clipPath: 'circle(30px at 40px 40px)',
//     transition: {
//       delay: 0.5,
//       type: 'spring',
//       stiffness: 400,
//       damping: 40
//     }
//   }
// };

const capitalize = (str?: string): string => {
  if (!str) {
    return '';
  }
  return str
    .split(/\s+/)
    .map((n) => `${n.slice(0, 1).toUpperCase()}${n.slice(1).toLowerCase()}`)
    .join(' ');
};

const UserSection = ({ user }: { user: User | null }) => (
  <Stack alignItems="center" gap={1} my={2}>
    <Box
      position="relative"
      fontSize={56}
      height="fit-content"
      textAlign="center"
    >
      <PersonOutlineTwoToneIcon fontSize={'inherit'} />
    </Box>
    <Stack alignItems="center">
      <Typography
        color="white"
        fontWeight={500}
        variant="subtitle2"
        fontSize={18}
        textAlign="center"
      >
        {capitalize(user?.name)}
      </Typography>
      <Typography color="silver" fontSize="small">
        {user?.username}
      </Typography>
    </Stack>
  </Stack>
);

export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 2),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'space-between'
}));

const NavBackground = styled(motion.div)`
  width: 100%;
  background: #1a202e;
  height: 100vh;
  overflow: hidden;
`;

function useCloseOnRouteChange(isXlAndUp: boolean, onClose: () => void) {
  const pathname = usePathname();
  const didMount = useRef(false);

  useEffect(() => {
    // Skip first render to mimic "routeChangeComplete" (only on navigations)
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    if (!isXlAndUp) {
      onClose();
    }
  }, [pathname, isXlAndUp, onClose]);
}

interface NavigationDrawerProps extends Omit<DrawerProps, 'children'> {
  drawerWidth: number;
  onClose: () => void;
}

export const NavigationDrawer = ({
  onClose,
  drawerWidth,
  ...props
}: NavigationDrawerProps) => {
  const isXlAndUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
  const translate = useTranslator();
  const dispatch = useAppDispatch();
  const user = useCurrentUser();

  useCloseOnRouteChange(isXlAndUp, onClose);

  return (
    <Drawer
      variant={isXlAndUp ? 'persistent' : 'temporary'}
      anchor="left"
      onClose={onClose}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          bgcolor: 'white',
          color: 'white'
        }
      }}
      {...props}
    >
      <nav>
        <NavBackground /*variants={sidebar}*/>
          <DrawerHeader>
            <Box
              display="flex"
              sx={{
                transform: 'scale(1.8)'
              }}
            >
              <Image
                src={Logo}
                alt="HoopCare logo"
                width={32}
                height={32}
                style={{ objectFit: 'contain', objectPosition: 'center' }}
              />
            </Box>
            <Box flexShrink={1}>
              <LangSelector />
            </Box>
            <Box>
              <Tooltip arrow title={translate('app.logout')}>
                <IconButton
                  sx={{ color: 'white' }}
                  onClick={() => dispatch(logout())}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </DrawerHeader>
          <Divider color="gray" />
          <UserSection user={user} />
          <NavigationList navItems={navItems} />
        </NavBackground>
      </nav>
    </Drawer>
  );
};
